"use client";

import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

type PaginationBarProps = {
  /** Precomputed page list; numbers render as links, strings render as an ellipsis. */
  pages: (number | string)[];
  current: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Optional href builder for real navigation / SEO; omit for onClick-only usage. */
  getPageUrl?: (page: number) => string | undefined;
  className?: string;
};

export const PaginationBar = ({
  pages,
  current,
  totalPages,
  onPageChange,
  getPageUrl,
  className,
}: PaginationBarProps) => {
  const isFirst = current === 1;
  const isLast = current === totalPages;

  return (
    <Pagination className={cn(className)}>
      <PaginationContent className="gap-1.5">
        <PaginationLink
          aria-label="Go to previous page"
          className={cn(
            "size-12 cursor-pointer rounded-xl border-0 bg-background-fill-brand-green hover:bg-background-fill-brand-green-hover",
            isFirst && "pointer-events-none cursor-default",
          )}
          href={isFirst ? undefined : getPageUrl?.(current - 1)}
          onClick={() => !isFirst && onPageChange(current - 1)}
          target="_self"
        >
          <IconChevronLeft className="*:stroke-white" stroke={1.5} />
        </PaginationLink>

        {pages.map((page, index) => (
          <PaginationItem key={`${page}-${index}`}>
            {typeof page === "string" ? (
              <PaginationEllipsis className="size-12 rounded-sm border bg-background-fill-primary text-text [&>svg]:size-4" />
            ) : (
              <PaginationLink
                className={cn(
                  "size-12 cursor-pointer rounded-sm border bg-background-fill-primary font-normal text-sm text-text",
                  current === page &&
                    "border-2 border-border-selected font-bold",
                )}
                href={getPageUrl?.(page)}
                onClick={() => onPageChange(page)}
                target="_self"
              >
                {page}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationLink
            aria-label="Go to next page"
            className={cn(
              "size-12 cursor-pointer rounded-xl border-0 bg-background-fill-brand-green hover:bg-background-fill-brand-green-hover",
              isLast && "pointer-events-none cursor-default",
            )}
            href={isLast ? undefined : getPageUrl?.(current + 1)}
            onClick={() => !isLast && onPageChange(current + 1)}
            target="_self"
          >
            <IconChevronRight className="*:stroke-white" stroke={1.5} />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};
